import { Box, LinearProgress, Tooltip, Typography } from "@mui/material";
import type { AnalysisJob, AnalysisProgress } from "@/features/analysis";
import type { ImportProgress } from "@/features/games";

type SidebarStatusSurfacesProps = {
  isAnalyzing: boolean;
  currentJob: AnalysisJob | undefined;
  currentProgress: AnalysisProgress | null;
  gamesCompleted: number;
  gamesTotal: number;
  overallProgress: number;
  remainingLabel: string;
  queuedCount: number;
  importProgress: ImportProgress | null;
  onStopAnalysis: () => void;
  onStopImport: () => void;
};

export const SidebarStatusSurfaces = ({
  isAnalyzing,
  currentJob,
  currentProgress,
  gamesCompleted,
  gamesTotal,
  overallProgress,
  remainingLabel,
  queuedCount,
  importProgress,
  onStopAnalysis,
  onStopImport,
}: SidebarStatusSurfacesProps) => (
  <>
    {isAnalyzing && (
      <Tooltip title="Click or tap to stop analyzing" placement="right" enterTouchDelay={0}>
        <Box
          component="button"
          type="button"
          onClick={onStopAnalysis}
          sx={{
            width: "100%",
            appearance: "none",
            textAlign: "left",
            color: "text.primary",
            font: "inherit",
            px: 2,
            py: 1.5,
            border: 0,
            borderRadius: 0,
            cursor: "pointer",
            bgcolor: "transparent",
            transition: "none",
            "&:hover": {
              bgcolor: "primary.main",
              color: "background.default",
              borderColor: "primary.main",
              "& .sidebar-analysis-accent, & .sidebar-analysis-detail": { color: "background.default" },
              "& .sidebar-analysis-stop": { color: "background.default", borderColor: "background.default" },
              "& .MuiLinearProgress-root": { bgcolor: "rgba(10,10,10,0.2)" },
              "& .MuiLinearProgress-bar": { bgcolor: "background.default" },
            },
            "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: -2 },
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 0.75 }}>
            <Typography
              className="sidebar-analysis-accent"
              variant="caption"
              sx={{ color: "primary.main", fontWeight: 800, letterSpacing: "0.06em" }}
            >
              NIKOLA ANALYZING
            </Typography>
            <Typography
              className="sidebar-analysis-stop"
              variant="caption"
              sx={{ color: "secondary.main", fontWeight: 700, borderBottom: "1px solid", borderColor: "transparent" }}
            >
              STOP
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: "inherit",
                display: "block",
                fontWeight: 700,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {currentJob ? `vs ${currentJob.opponent}` : "Preparing your games"}
            </Typography>
            <Typography
              className="sidebar-analysis-accent"
              variant="caption"
              sx={{ color: "primary.main", fontWeight: 800, flexShrink: 0 }}
            >
              {Math.round(Math.min(100, overallProgress))}%
            </Typography>
          </Box>
          <Typography
            className="sidebar-analysis-detail"
            variant="caption"
            sx={{ color: "text.secondary", display: "block", mt: 0.25 }}
          >
            {currentProgress
              ? `${currentProgress.currentMove}/${currentProgress.totalMoves} moves · ${gamesCompleted + 1}/${gamesTotal} games`
              : `${gamesCompleted}/${gamesTotal} games complete`}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, overallProgress)}
            sx={{
              height: 5,
              my: 1,
              bgcolor: "rgba(255,255,255,0.08)",
              "& .MuiLinearProgress-bar": { bgcolor: "primary.main" },
            }}
          />
          <Typography
            className="sidebar-analysis-detail"
            variant="caption"
            sx={{ color: "text.secondary", display: "block", fontSize: "0.65rem" }}
          >
            {remainingLabel} left · {queuedCount} queued
          </Typography>
        </Box>
      </Tooltip>
    )}
    {importProgress && (
      <Tooltip title="Click or tap to stop importing" placement="right" enterTouchDelay={0}>
        <Box
          component="button"
          type="button"
          onClick={onStopImport}
          sx={{
            width: "100%",
            appearance: "none",
            textAlign: "left",
            color: "text.primary",
            font: "inherit",
            px: 2,
            py: 1.5,
            border: 0,
            cursor: "pointer",
            bgcolor: "transparent",
            "&:hover": {
              bgcolor: "primary.main",
              color: "background.default",
              "& .sidebar-import-detail": { color: "background.default" },
              "& .MuiLinearProgress-bar": { bgcolor: "background.default" },
            },
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 0.75 }}>
            <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 800, letterSpacing: "0.06em" }}>
              NIKOLA IMPORTING
            </Typography>
            <Typography variant="caption" sx={{ color: "secondary.main", fontWeight: 700 }}>
              STOP
            </Typography>
          </Box>
          <Typography
            className="sidebar-import-detail"
            variant="caption"
            sx={{ color: "text.secondary", display: "block" }}
          >
            {importProgress.imported}
            {importProgress.target != null ? ` / ${importProgress.target}` : ""} games brought in
          </Typography>
          <LinearProgress
            variant={importProgress.target == null ? "indeterminate" : "determinate"}
            value={importProgress.target ? (importProgress.imported / importProgress.target) * 100 : undefined}
            sx={{
              height: 5,
              my: 1,
              bgcolor: "rgba(255,255,255,0.08)",
              "& .MuiLinearProgress-bar": { bgcolor: "primary.main" },
            }}
          />
          <Typography
            className="sidebar-import-detail"
            variant="caption"
            sx={{ color: "text.secondary", display: "block", fontSize: "0.65rem" }}
          >
            Nikola is tracing your next games.
          </Typography>
        </Box>
      </Tooltip>
    )}
  </>
);
